/**
 * Inventory availability utilities
 *
 * @module utils/inventory
 */

export const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Get inventory quantity for a variant from Liquid-rendered data.
 *
 * @param {object} variant Variant data.
 * @returns {number|null} Inventory quantity or null when not tracked in Liquid data.
 */
export function getVariantInventoryQuantity(variant) {
    if (!variant || !window.productsInventoryQuantity) {
        return null;
    }

    const entry = window.productsInventoryQuantity.find(item => {
        const [key] = item.split(':');
        return key == variant.id;
    });

    if (!entry) {
        return null;
    }

    const [, value] = entry.split(':');
    const quantity = parseInt(value, 10);

    return isNaN(quantity) ? null : quantity;
}

/**
 * Check if a variant is available.
 * Priority: productsInventoryQuantity > 0, fallback to variant.available.
 *
 * @param {object} variant Variant data.
 * @returns {boolean}
 */
export function isVariantAvailable(variant) {
    if (!variant) {
        return false;
    }

    const quantity = getVariantInventoryQuantity(variant);

    if (quantity !== null) {
        return quantity > 0;
    }

    return Boolean(variant.available);
}

/**
 * Sync variant.available on a product using unified inventory logic.
 * Only overrides availability when Liquid inventory data exists for a variant.
 *
 * @param {object} product Product data.
 * @returns {undefined}
 */
export function syncVariantAvailability(product) {
    if (!product || !product.variants) {
        return;
    }

    product.variants.forEach(variant => {
        const quantity = getVariantInventoryQuantity(variant);

        if (quantity !== null) {
            variant.available = quantity > 0;
        }
    });
}
